import React, { useState } from "react";

import { CircularProgress } from "@material-ui/core";

import { API, graphqlOperation } from "aws-amplify";
import { Button, useNotify, useRefresh } from "react-admin";

import { ImportIcon } from "../../../components/common/iconography";
import { createCarrierWithIdentifiers } from "../../../graphql/carrier/carrier-mutations";
import { logError } from "../../../utils/appUtils";
import {
  buildCarrierIdentityFields,
  clearCarrierIdentityCache,
  describeCarrierConflict,
  fetchCarrierIdentities,
  findCarrierConflict,
} from "../../../utils/carrierIdentity";
import { publishCreatedCarrier } from "../../../utils/carrierListCache";
import { CarrierImportDialog } from "./CarrierImportDialog";
import { describeImportSummary } from "./carrierImportUtils";
import {
  notifyPrimaryInviteResult,
  sendPrimaryDispatchInvite,
} from "./primaryDispatchInvite";

// How many skipped rows to name in the summary before it stops listing them.
const MAX_NAMED_SKIPS = 3;

/**
 * Import carriers from a CSV, and invite each one's address as its primary
 * dispatch user.
 *
 * The same two steps the Carriers form does one at a time (createCarrier, then
 * sendPrimaryDispatchInvite), run over a reviewed batch. Rows are written one at
 * a time on purpose: a row that fails is reported and the rest still import,
 * where a bulk write would leave the manager guessing which carriers landed.
 *
 * The duplicate check runs again here against a fresh fetch of the yard's book —
 * the dialog's copy was loaded before the file was even mapped, and another user
 * may have added a carrier since. It fails closed: no book, no import.
 */
export const CarrierImportButton = ({ disabled: disabledProp }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const createOneCarrier = async (values) => {
    const response = await API.graphql(
      graphqlOperation(createCarrierWithIdentifiers, {
        input: {
          active: "true",
          reloadYardId: window.appUser?.reloadYardId,
          groups: window.appUser?.groups,
          businessName: values.businessName,
          email: values.email,
          ...(values.phone ? { phone: values.phone } : {}),
          ...buildCarrierIdentityFields(values),
          // Mapped and normalised by planCarrierImport, and left off entirely
          // when the file had no USDOT column to map.
          ...(values.identifiers?.length
            ? { identifiers: values.identifiers }
            : {}),
        },
      }),
    );
    return response.data.createCarrier;
  };

  const processImport = async (rows) => {
    if (!rows?.length) return;

    setLoading(true);
    let book;
    try {
      book = await fetchCarrierIdentities(window.appUser?.reloadYardId);
    } catch (error) {
      logError("managerApp:carriers:CarrierImportButton:fetchBook", error, false);
      notify(
        "Could not check for existing carriers. Nothing was imported — please try again.",
        "warning",
      );
      setLoading(false);
      return;
    }

    const summary = { created: 0, invited: 0, skipped: 0, failed: 0 };
    const notes = [];

    for (const values of rows) {
      const conflict = findCarrierConflict(book, values);
      if (conflict) {
        // Added between the dialog's review and now.
        summary.skipped += 1;
        notes.push(
          `${values.businessName}: ${describeCarrierConflict(conflict)}`,
        );
        continue;
      }

      let carrier;
      try {
        // eslint-disable-next-line no-await-in-loop
        carrier = await createOneCarrier(values);
        summary.created += 1;
        book.push(carrier);
        // Keep the shared list and the on-blur duplicate book current, as every
        // other create path does.
        publishCreatedCarrier(carrier);
        clearCarrierIdentityCache();
      } catch (error) {
        summary.failed += 1;
        notes.push(`${values.businessName}: could not be created.`);
        logError("managerApp:carriers:CarrierImportButton:create", error, false);
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await sendPrimaryDispatchInvite(carrier);
        if (result.sent) summary.invited += 1;
        else if (rows.length === 1) notifyPrimaryInviteResult(notify, carrier, result);
        else notes.push(`${carrier.businessName}: no invite sent.`);
      } catch (error) {
        // The carrier stands either way — Send Invite on the carrier retries.
        logError("managerApp:carriers:CarrierImportButton:invite", error, false);
        notes.push(`${carrier.businessName}: invite could not be sent.`);
      }
    }

    setLoading(false);
    refresh();

    notify(
      describeImportSummary(summary),
      summary.failed || summary.skipped ? "warning" : "info",
    );
    if (notes.length > 0) {
      const named = notes.slice(0, MAX_NAMED_SKIPS).join(" ");
      const rest = notes.length - MAX_NAMED_SKIPS;
      notify(rest > 0 ? `${named} (+${rest} more)` : named, "warning");
    }
  };

  return (
    <>
      <Button
        label="Import"
        onClick={() => setImportOpen(true)}
        color="primary"
        size="small"
        disabled={disabledProp || loading}
      >
        {loading ? (
          <CircularProgress color="inherit" size={17} />
        ) : (
          <ImportIcon />
        )}
      </Button>

      {importOpen && (
        <CarrierImportDialog
          isOpen={importOpen}
          setIsOpen={setImportOpen}
          handleImport={processImport}
        />
      )}
    </>
  );
};
